import { useEffect, useState } from "react";

import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { Switch } from "~/components/ui/switch";
import type { Hallway, HallwayFormData } from "~/types/wms";

interface HallwayDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  hallway?: Hallway;
  warehouseId?: string;
  onSubmit: (data: HallwayFormData) => void;
  isLoading: boolean;
}

export function HallwayDialog({
  open,
  onOpenChange,
  hallway,
  warehouseId,
  onSubmit,
  isLoading,
}: HallwayDialogProps) {
  const [formData, setFormData] = useState<HallwayFormData>({
    warehouse_id: "",
    code: "",
    name: "",
    sequence: 1,
    is_active: true,
  });

  useEffect(() => {
    if (hallway) {
      setFormData({
        warehouse_id: hallway.warehouse_id,
        code: hallway.code,
        name: hallway.name || "",
        sequence: hallway.sequence,
        is_active: hallway.is_active,
      });
    } else {
      setFormData({
        warehouse_id: warehouseId || "",
        code: "",
        name: "",
        sequence: 1,
        is_active: true,
      });
    }
  }, [hallway, warehouseId, open]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>
              {hallway ? "Editar Pasillo" : "Nuevo Pasillo"}
            </DialogTitle>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="code" className="text-right">
                Código *
              </Label>
              <Input
                id="code"
                value={formData.code}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    code: e.target.value.toUpperCase(),
                  })
                }
                className="col-span-3"
                placeholder="HA"
                required
              />
            </div>

            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="name" className="text-right">
                Nombre
              </Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                className="col-span-3"
                placeholder="Pasillo A"
              />
            </div>

            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="sequence" className="text-right">
                Secuencia
              </Label>
              <Input
                id="sequence"
                type="number"
                min={1}
                value={formData.sequence}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    sequence: parseInt(e.target.value) || 1,
                  })
                }
                className="col-span-3"
              />
            </div>

            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="is_active" className="text-right">
                Activo
              </Label>
              <div className="col-span-3">
                <Switch
                  id="is_active"
                  checked={formData.is_active}
                  onCheckedChange={(checked) =>
                    setFormData({ ...formData, is_active: checked })
                  }
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? "Guardando..." : hallway ? "Guardar" : "Crear"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
